/**
 * Safely extracts the impactScore record from ShareableImage metadata.
 * Handles both parsed object and JSON-encoded string forms.
 */
export function getImpactScore(
  metadata: Record<string, any> | string | undefined | null,
): Record<string, number> | undefined {
  if (!metadata) return undefined;
  try {
    const parsed =
      typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    return parsed?.impactScore;
  } catch {
    return undefined;
  }
}

/**
 * Extracts the main impact score value from metadata for sorting.
 * Returns -Infinity for items without scores so they sort to the end.
 */
export function extractMainImpactScore(
  metadata: Record<string, any> | string | undefined | null,
): number {
  const impactScore = getImpactScore(metadata);
  if (!impactScore || Object.keys(impactScore).length === 0) {
    return -Infinity;
  }
  const { main } = destructureImpactScore(impactScore);
  return main ?? -Infinity;
}

/**
 * Destructures an impact score object into a main score and remaining metrics.
 *
 * Looks for a key containing "total" or "final" as the main metric;
 * falls back to the first key if neither is found.
 */
export function destructureImpactScore(
  impactScore: Record<string, number> | undefined,
): Record<string, number> {
  if (!impactScore) {
    return { main: 0 };
  }

  let mainKey = Object.keys(impactScore).find(
    key =>
      key.toLowerCase().includes('total') ||
      key.toLowerCase().includes('final'),
  );
  if (!mainKey) {
    mainKey = Object.keys(impactScore)[0];
  }

  const rest = Object.keys(impactScore).reduce(
    (acc, key) => {
      if (key !== mainKey) {
        acc[key] = impactScore[key];
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  return { main: impactScore[mainKey], ...rest };
}

/**
 * Averages a set of impactScore records (per-key average across clips).
 * Skips entries without a score and ignores non-numeric/NaN values.
 * Returns undefined when no input clip had any score.
 */
export function averageImpactScores(
  scores: (Record<string, number> | undefined)[],
): Record<string, number> | undefined {
  const totals: Record<string, { sum: number; count: number }> = {};
  for (const score of scores) {
    if (!score) continue;
    for (const [key, value] of Object.entries(score)) {
      if (typeof value !== 'number' || Number.isNaN(value)) continue;
      if (!totals[key]) totals[key] = { sum: 0, count: 0 };
      totals[key].sum += value;
      totals[key].count += 1;
    }
  }
  const keys = Object.keys(totals);
  if (keys.length === 0) return undefined;
  return keys.reduce<Record<string, number>>((acc, key) => {
    acc[key] = Math.round(totals[key].sum / totals[key].count);
    return acc;
  }, {});
}
